import { useState, useCallback, useEffect } from 'react';
import { useDataProvider, useNotify, Identifier } from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import {
    Box,
    Stack,
    Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import type { CardAttachment } from '@/types';
import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import type { QuoteAttachment } from '@/utils/quote_attachment_utils';
import { FileDropzone } from '@/components/misc/FileDropzone';
import { MAX_FILE_SIZE, MAX_FILES, CARD_ACCEPT } from './cardAttachmentRules';

interface CardAttachmentListProps {
    cardId: Identifier;
    attachments: CardAttachment[];
    canRemove: boolean;
    canUpload?: boolean;
    onChanged?: () => void;
}

export const CardAttachmentList: React.FC<CardAttachmentListProps> = ({
    cardId,
    attachments,
    canRemove,
    canUpload = false,
    onChanged,
}) => {
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState<Identifier | null>(null);
    const [previewAttachment, setPreviewAttachment] = useState<QuoteAttachment | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handlePreview = useCallback(async (att: CardAttachment) => {
        const asQuoteAttachment: QuoteAttachment = {
            id: String(att.id),
            name: att.filename,
            filename: att.filename,
            content_type: att.content_type,
        };
        setPreviewAttachment(asQuoteAttachment);
        setPreviewUrl(null);
        setPreviewLoading(true);
        try {
            const { blob } = await dataProvider.getCardAttachmentBlob(cardId, att.id);
            setPreviewUrl(URL.createObjectURL(blob));
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to open file';
            notify(message, { type: 'error' });
            setPreviewAttachment(null);
        } finally {
            setPreviewLoading(false);
        }
    }, [cardId, notify]);

    const handlePreviewClose = useCallback(() => {
        setPreviewAttachment(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    }, [previewUrl]);

    const handleDelete = useCallback(async (attId: Identifier) => {
        if (!window.confirm('Delete this attachment?')) return;

        setDeleting(attId);
        try {
            await dataProvider.deleteCardAttachment(cardId, attId);
            notify('Attachment deleted', { type: 'success' });
            onChanged?.();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete';
            notify(message, { type: 'error' });
        } finally {
            setDeleting(null);
        }
    }, [cardId, notify, onChanged]);

    // The dropzone enforces type/size/count and hands over valid files; upload them straight
    // to the existing card (it is a stateless picker here, so we never hold them in state).
    const handleDropFiles = useCallback(async (files: File[]) => {
        if (files.length === 0) return;

        setUploading(true);
        try {
            await dataProvider.uploadCardFiles(cardId, files);
            notify('Files uploaded', { type: 'success' });
            onChanged?.();
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Upload failed';
            notify(message, { type: 'error' });
        } finally {
            setUploading(false);
        }
    }, [cardId, notify, onChanged]);

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {attachments.map(att => (
                    <Chip
                        key={att.id}
                        icon={<DownloadIcon />}
                        label={att.filename}
                        onClick={() => handlePreview(att)}
                        onDelete={canRemove && deleting !== att.id ? () => handleDelete(att.id) : undefined}
                        disabled={deleting === att.id || uploading}
                        variant="outlined"
                    />
                ))}
            </Stack>

            {canUpload && (
                <FileDropzone
                    value={[]}
                    onChange={handleDropFiles}
                    maxFiles={MAX_FILES}
                    maxSize={MAX_FILE_SIZE}
                    accept={CARD_ACCEPT}
                    disabled={uploading}
                    label="Drop files here, or click to add"
                    showHint
                />
            )}
            <QuoteAttachmentPreviewDialog
                attachment={previewAttachment}
                sourceUrl={previewUrl}
                loading={previewLoading}
                onClose={handlePreviewClose}
            />
        </Box>
    );
};

CardAttachmentList.displayName = 'CardAttachmentList';
